import { matchSorter, rankings } from "match-sorter";

import type { HierarchyPickerOption } from "./HierarchyPicker.types";

// Assumes both arrays contain unique elements (no duplicates).
export const hasSameItems = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  const leftSet = new Set(left);
  const rightSet = new Set(right);

  if (leftSet.size !== rightSet.size) {
    return false;
  }

  return Array.from(leftSet).every((item) => rightSet.has(item));
};

export const getNextExpandedItems = (
  current: string[],
  defaultExpandedItems: string[] | undefined,
  rootIds: string[],
): string[] => {
  if (defaultExpandedItems !== undefined) {
    return hasSameItems(current, defaultExpandedItems) ? current : defaultExpandedItems;
  }

  if (!rootIds.length && !current.length) {
    return current;
  }

  const next = new Set(current);
  let hasNewRoot = false;
  rootIds.forEach((rootId) => {
    if (!next.has(rootId)) {
      next.add(rootId);
      hasNewRoot = true;
    }
  });

  return hasNewRoot ? Array.from(next) : current;
};

type HierarchyPickerGetters<TOption extends HierarchyPickerOption> = {
  getOptionChildren: (option: TOption) => TOption[] | undefined;
  getOptionId: (option: TOption) => string;
  getOptionLabel: (option: TOption) => string;
};

type HierarchySearchRecord<TOption extends HierarchyPickerOption> = {
  ancestorIds: string[];
  descendantIds: string[];
  id: string;
  label: string;
  option: TOption;
  path: string;
};

export type HierarchySearchMetadata<TOption extends HierarchyPickerOption = HierarchyPickerOption> = {
  optionMap: Map<string, TOption>;
  searchRecords: HierarchySearchRecord<TOption>[];
};

export const buildHierarchySearchMetadata = <TOption extends HierarchyPickerOption>(
  options: TOption[],
  { getOptionChildren, getOptionId, getOptionLabel }: HierarchyPickerGetters<TOption>,
): HierarchySearchMetadata<TOption> => {
  const optionMap = new Map<string, TOption>();
  const searchRecords: HierarchySearchRecord<TOption>[] = [];

  const visit = (nodes: TOption[], ancestorIds: string[], ancestorLabels: string[]): string[] => {
    const subtreeIds: string[] = [];

    nodes.forEach((node) => {
      const id = getOptionId(node);
      const label = getOptionLabel(node);
      const pathLabels = [...ancestorLabels, label];
      const children = getOptionChildren(node) ?? [];
      const record: HierarchySearchRecord<TOption> = {
        ancestorIds,
        descendantIds: [],
        id,
        label,
        option: node,
        path: pathLabels.join(" > "),
      };

      optionMap.set(id, node);
      searchRecords.push(record);

      const descendantIds = visit(children, [...ancestorIds, id], pathLabels);

      record.descendantIds = descendantIds;
      subtreeIds.push(id, ...descendantIds);
    });

    return subtreeIds;
  };

  visit(options, [], []);

  return {
    optionMap,
    searchRecords,
  };
};

const buildFilteredHierarchy = <TOption extends HierarchyPickerOption>(
  nodes: TOption[],
  includedIds: Set<string>,
  {
    getOptionChildren,
    getOptionId,
  }: Pick<HierarchyPickerGetters<TOption>, "getOptionChildren" | "getOptionId">,
): TOption[] => {
  return nodes.flatMap((node) => {
    const id = getOptionId(node);

    if (!includedIds.has(id)) {
      return [];
    }

    const children = getOptionChildren(node) ?? [];
    if (!children.length) {
      return [node];
    }

    const filteredChildren = buildFilteredHierarchy(children, includedIds, {
      getOptionChildren,
      getOptionId,
    });

    return [
      {
        ...node,
        children: filteredChildren.length ? filteredChildren : undefined,
      } as TOption,
    ];
  });
};

const collectExpandedIds = <TOption extends HierarchyPickerOption>(
  nodes: TOption[],
  {
    getOptionChildren,
    getOptionId,
  }: Pick<HierarchyPickerGetters<TOption>, "getOptionChildren" | "getOptionId">,
): string[] => {
  const expandedIds: string[] = [];

  const visit = (tree: TOption[]) => {
    tree.forEach((node) => {
      const children = getOptionChildren(node) ?? [];

      if (!children.length) {
        return;
      }

      expandedIds.push(getOptionId(node));
      visit(children);
    });
  };

  visit(nodes);

  return expandedIds;
};

export const getHierarchySearchResult = <TOption extends HierarchyPickerOption>(
  options: TOption[],
  query: string,
  metadata: HierarchySearchMetadata<TOption>,
  getters: HierarchyPickerGetters<TOption>,
) => {
  const trimmedQuery = query.trim();

  if (!trimmedQuery) {
    return {
      expandedIds: [],
      filteredOptions: options,
    };
  }

  const matchingRecords = matchSorter(metadata.searchRecords, trimmedQuery, {
    keys: ["label", "path"],
    threshold: rankings.CONTAINS,
  });
  const includedIds = new Set<string>();

  matchingRecords.forEach((record) => {
    record.ancestorIds.forEach((ancestorId) => {
      includedIds.add(ancestorId);
    });
    includedIds.add(record.id);
    record.descendantIds.forEach((descendantId) => {
      includedIds.add(descendantId);
    });
  });

  const filteredOptions = buildFilteredHierarchy(options, includedIds, getters);

  return {
    expandedIds: collectExpandedIds(filteredOptions, getters),
    filteredOptions,
  };
};
